import { AgBarSeriesTooltipRendererParams, AgCartesianChartOptions } from "ag-charts-community";
import { useMemo } from "react";

import { DEFAULT_BASE_CHART_CONFIG } from "../../constants";
import { formatNumberWithSuffix } from "../../hooks/utils";
import { DeploymentSeriesConfig, DeploymentUsageChartDataPoint } from "../types";
import { getSeriesValue } from "../utils";

import { formatSpacedCategoryAxisLabel } from "@/utils/dateAxisCategoryLabels";

interface MutableAxis {
  label?: {
    formatter?: (params: { value: number | string; index: number }) => string;
  };
}

export const useDeploymentUsageChartOptions = ({
  chartData,
  deploymentSeries,
}: {
  chartData: DeploymentUsageChartDataPoint[];
  deploymentSeries: DeploymentSeriesConfig[];
}) =>
  useMemo<AgCartesianChartOptions>(() => {
    const baseConfig = JSON.parse(
      JSON.stringify(DEFAULT_BASE_CHART_CONFIG),
    ) as AgCartesianChartOptions & {
      axes: MutableAxis[];
    };

    baseConfig.axes[1].label = {
      ...baseConfig.axes[1].label,
      formatter: ({ value }: { value: number | string }) => formatNumberWithSuffix(Number(value)),
    };
    baseConfig.axes[0].label = {
      ...baseConfig.axes[0].label,
      formatter: ({ value, index }: { value: number | string; index: number }) =>
        formatSpacedCategoryAxisLabel({
          datapointCount: chartData.length,
          value,
          index,
        }),
    };

    return {
      ...baseConfig,
      data: chartData,
      series: deploymentSeries.map((seriesConfig, index) => ({
        type: "bar" as const,
        xKey: "datetime",
        yKey: seriesConfig.dataKey,
        yName: seriesConfig.displayName,
        stacked: true,
        strokeWidth: 0,
        marker: {
          enabled: false,
        },
        tooltip: {
          renderer: ({
            datum,
            fill,
          }: AgBarSeriesTooltipRendererParams<DeploymentUsageChartDataPoint>) => {
            const value = getSeriesValue(datum, seriesConfig.dataKey);
            const sessionValue = getSeriesValue(datum, seriesConfig.sessionDataKey);
            const markerColor = typeof fill === "string" ? fill : "#9CA3AF";

            return `
              <div style="display: flex; flex-direction: column; gap: 4px;">
                ${
                  index === 0
                    ? `<div style="padding: 4px 8px; font-weight: 500; color: #374151;">
                        ${datum.datetime}
                      </div>`
                    : ""
                }
                <div style="padding: 0px 8px 4px;">
                  <div style="display: flex; justify-content: space-between; gap: 12px;">
                    <div style="display: flex; gap: 6px;">
                      <div style="width: 10px; height: 10px; border-radius: 9999px; background-color: ${markerColor}; margin-top: 3px; flex-shrink: 0;"></div>
                      <div style="display: flex; flex-direction: column; gap: 2px;">
                        <span style="color: #111827; font-weight: 500;">${seriesConfig.deploymentName}</span>
                        <span style="color: #6B7280; font-size: 12px;">${seriesConfig.metadataLabel}</span>
                      </div>
                    </div>
                    <div style="font-weight: 500; color: #374151; text-align: right; white-space: nowrap;">
                      ${value.toLocaleString()} tokens / ${sessionValue.toLocaleString()} sessions
                    </div>
                  </div>
                </div>
              </div>
            `;
          },
        },
      })),
    };
  }, [chartData, deploymentSeries]);
